import React, { FC, useState, RefObject } from "react";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import {
  BlockHeader,
  HeaderLevel,
} from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { Plus } from "@app/components/common/icons";
import { checkFileType, FileType } from "@app/helpers/check-file-type";
import { CarouselCard } from "./types";

type Props = {
  headerRef?: RefObject<HTMLDivElement>;
  paragraphRef?: RefObject<HTMLDivElement>;
} & CarouselCard;

export const Card: FC<Props> = ({
  title,
  backsideText,
  headerTitle,
  text,
  icon,
  image,
  headerRef,
  paragraphRef,
}) => {
  const [isBacksideVisible, setIsBacksideVisible] = useState(false);
  const iconType = icon?.filename ? checkFileType(icon.filename) : undefined;

  return (
    <div
      className={classNames(
        "relative mr-5 min-w-0 shrink-0 grow-0 basis-[316px] rounded-2xl [transform:translate3d(0,0,0)]",
        "aspect-[7/10] overflow-hidden border border-divider text-primary-content",
        "lg:mr-6 lg:basis-[290px]",
        "xxl:mr-7.5 xxl:basis-[478px] xxl:rounded-3xl",
      )}
    >
      <div
        className={classNames("relative h-full duration-700", {
          "-translate-y-full": !isBacksideVisible,
        })}
      >
        <div
          className={classNames(
            "flex h-full w-full flex-col justify-end",
            "bg-dark px-6 pb-8",
            "xxl:px-10 xxl:pb-18",
          )}
        >
          <div className={classNames("text-medium", "xxl:text-large")}>
            <BlockBody richText={backsideText} />
          </div>
        </div>
        <div
          className={classNames(
            "relative h-full w-full bg-base-content",
            "flex flex-col justify-end",
            "px-6 pb-8 pt-9",
            "lg:pt-8",
            "xxl:px-10 xxl:pb-14 xxl:pt-13",
          )}
        >
          <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full [transform:translate3d(0,0,0)]">
            {image?.filename ? (
              <GatsbyImage
                image={getGatsbyImage(image.filename, { layout: "fullWidth" })}
                alt={image.alt}
                className="h-full w-full"
              />
            ) : null}
            <div
              className={classNames(
                "absolute bottom-0 left-0 right-0 top-0 h-full [transform:translate3d(0,0,0)]",
                {
                  "bg-base-content/30": image?.filename,
                  hidden: !image?.filename,
                },
              )}
            />
            <BlockHeader
              richText={headerTitle}
              headerLevel={HeaderLevel.H3}
              className={classNames(
                "absolute left-6 top-10 max-w-[65%] text-regular [transform:translate3d(0,0,0)]",
                "lg:top-[38px] lg:text-extra-small",
                "xxl:left-10 xxl:top-15 xxl:text-medium",
              )}
            />
          </div>
          <div
            className={classNames(
              "relative text-regular text-primary-content [transform:translate3d(0,0,0)]",
              "lg:text-extra-small",
              "xxl:text-regular",
            )}
          >
            {iconType ? (
              <div
                className={classNames(
                  "mb-5 flex min-h-7.5 items-center",
                  "lg:min-h-6",
                  "xxl:min-h-9",
                )}
              >
                {iconType === FileType.SVG ? (
                  <img
                    src={icon.filename}
                    className={classNames("h-7.5", "lg:h-6", "xxl:h-9")}
                  />
                ) : iconType === FileType.IMAGE ? (
                  <GatsbyImage
                    image={getGatsbyImage(icon.filename, { layout: "fixed" })}
                    alt={icon.alt}
                    className={classNames("h-7.5", "lg:h-6", "xxl:h-9")}
                  />
                ) : null}
              </div>
            ) : null}

            <div ref={headerRef} className="mb-3">
              <BlockHeader
                richText={title}
                headerLevel={HeaderLevel.H4}
                className={classNames("text-medium", "xxl:text-large")}
              />
            </div>
            <div ref={paragraphRef}>
              <BlockBody richText={text} />
            </div>
          </div>
        </div>
      </div>
      <button
        role="button"
        onClick={() => setIsBacksideVisible(!isBacksideVisible)}
        className={classNames(
          "absolute right-6 top-8 flex h-9 w-9 items-center justify-center rounded-full bg-base-100 bg-opacity-[15%] [transform:translate3d(0,0,0)]",
          "lg:h-8 lg:w-8",
          "xxl:right-10 xxl:top-11 xxl:h-13 xxl:w-13",
        )}
      >
        <Plus
          className={classNames(
            "relative h-3 w-3 stroke-primary-content duration-700",
            "xxl:h-5 xxl:w-5",
            { "rotate-45": isBacksideVisible, "rotate-0": !isBacksideVisible },
          )}
        />
      </button>
    </div>
  );
};
